import React, { useEffect, useState } from 'react';
import { enable_push, last_push_sync, push_enrol_state, read_push_env, sync_push_if_granted } from '../utils/comms_push';
import FaIcon from './FaIcon';

/**
 * App-shell enrolment banner for web push (Nikhil 2026-09-12: reps miss
 * client messages — one staff push subscription existed across the team,
 * ~1k deliveries/week suppressed as "no push subscription"). Mounted once
 * in layouts/Admin.jsx above the routed content, for every CS user whose
 * browser is not enrolled. Dismiss hides it for the browser session only.
 *
 * States (utils/comms_push.push_enrol_state):
 *   prompt       one button → enable_push()
 *   ios_install  iPhone/iPad in a Safari tab: web push only exists once the
 *                site is on the Home Screen → 3-step guide instead of a button
 *   unsupported  no push in this browser → say which to use
 *   denied       blocked in the browser → how to re-enable
 *   granted      permission ok → re-run the silent sync on mount; the banner
 *                stays hidden while that runs and for good once it succeeds
 */

const DISMISS_KEY = 'csp_push_banner_dismissed';
const read_dismissed = () => { try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; } };
const write_dismissed = () => { try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* storage blocked */ } };

const S = {
    bar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '5px 5px 0 0', padding: '9px 16px', background: '#fff8e1', border: '1px solid #f0d78b', borderRadius: 5, fontSize: 13, color: '#4a3b12', lineHeight: '19px' },
    ok: { background: '#eefaf2', border: '1px solid #cfeedd', color: '#2e7d4f' },
    text: { flex: 1, minWidth: 260 },
    title: { fontWeight: 600 },
    sub: { fontSize: 12, color: '#6d5c2e', marginTop: 1 },
    steps: { margin: '3px 0 0 18px', padding: 0, fontSize: 12.5 },
    button: { border: '1px solid #2f6fed', background: '#2f6fed', color: '#fff', borderRadius: 14, padding: '4px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', lineHeight: '18px', whiteSpace: 'nowrap' },
    close: { border: 'none', background: 'transparent', color: '#8a7a4a', fontSize: 16, lineHeight: 1, padding: '2px 4px', cursor: 'pointer' },
    error: { fontSize: 12, color: '#b03a2e', marginTop: 2 },
};

const ERROR_TEXT = {
    denied: 'Permission was not granted — allow notifications in the browser prompt and try again.',
    unavailable: 'Notifications are not available right now (the server has no push key configured). Try again later.',
};

export default function PushEnrolBanner() {
    const [state, set_state] = useState(() => push_enrol_state(read_push_env()));
    // 'checking' while a granted browser re-syncs (never flash the banner at
    // an already-enrolled rep); 'subscribed' hides it for good; 'failed' shows
    // the prompt so the rep can enrol by hand.
    const [sync, set_sync] = useState(() => {
        if (last_push_sync() === 'subscribed') return 'subscribed';
        return state === 'granted' ? 'checking' : 'idle';
    });
    const [dismissed, set_dismissed] = useState(read_dismissed);
    const [busy, set_busy] = useState(false);
    const [error, set_error] = useState(null);
    const [just_enabled, set_just_enabled] = useState(false);

    // Re-run the silent sync whenever the banner mounts (index.js runs it on
    // load too; comms_push shares one in-flight attempt between the two).
    // Mount-only on purpose: `state`/`sync` are read once, at mount.
    useEffect(() => {
        if (state !== 'granted' || sync === 'subscribed') return undefined;
        let alive = true;
        sync_push_if_granted().then((ok) => { if (alive) set_sync(ok ? 'subscribed' : 'failed'); });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!just_enabled) return undefined;
        const t = setTimeout(() => set_sync('subscribed'), 3000);
        return () => clearTimeout(t);
    }, [just_enabled]);

    const on_enable = async () => {
        set_busy(true);
        set_error(null);
        try {
            const r = await enable_push();
            if (r === 'subscribed') { set_just_enabled(true); return; }
            set_error(ERROR_TEXT[r] || 'Could not turn on notifications.');
            // Permission may have just been blocked — reflect the new browser state.
            set_state(push_enrol_state(read_push_env()));
        } catch (e) {
            set_error('Could not turn on notifications: ' + ((e && e.message) || 'unknown error'));
        } finally {
            set_busy(false);
        }
    };

    const dismiss = () => { write_dismissed(); set_dismissed(true); };

    if (dismissed || sync === 'subscribed' || sync === 'checking') return null;

    if (just_enabled) {
        return (
            <div role='status' style={{ ...S.bar, ...S.ok }}>
                <span style={{ fontSize: 16 }}><FaIcon name='bell' /></span>
                <div style={S.text}><span style={S.title}>Notifications are on</span> — client messages will reach you in this browser.</div>
            </div>
        );
    }

    let body;
    let action = null;
    if (state === 'ios_install') {
        body = (
            <>
                <div style={S.title}>Turn on notifications so client messages reach you</div>
                <div style={S.sub}>On iPhone and iPad, notifications only work once the CSP is on your Home Screen:</div>
                <ol style={S.steps}>
                    <li>In Safari, tap <b>Share</b> (the square with an arrow).</li>
                    <li>Tap <b>Add to Home Screen</b>, then <b>Add</b>.</li>
                    <li>Open <b>CSP</b> from the Home Screen and tap <b>Turn on notifications</b> here.</li>
                </ol>
            </>
        );
    } else if (state === 'unsupported') {
        body = (
            <>
                <div style={S.title}>This browser cannot receive client-message notifications</div>
                <div style={S.sub}>Use Chrome, Edge or Firefox on desktop or Android — or on iPhone/iPad, Safari 16.4+ with the CSP added to the Home Screen.</div>
            </>
        );
    } else if (state === 'denied') {
        body = (
            <>
                <div style={S.title}>Notifications are blocked for this site — client messages will not reach you</div>
                <div style={S.sub}>
                    Re-enable them: click the lock / tune icon left of the address bar → <b>Notifications</b> → <b>Allow</b>, then reload this page.
                    On a Home-Screen app on iPhone: <b>Settings → Notifications → CSP → Allow</b>.
                </div>
            </>
        );
    } else {
        body = (
            <>
                <div style={S.title}>Turn on notifications so client messages reach you</div>
                <div style={S.sub}>
                    {state === 'granted'
                        ? 'Notifications are allowed, but this browser is not registered yet.'
                        : 'One click — WhatsApp-bridged client messages then pop up here even when the CSP tab is in the background.'}
                </div>
                {error ? <div style={S.error}>{error}</div> : null}
            </>
        );
        action = (
            <button type='button' style={{ ...S.button, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={on_enable}>
                {busy ? 'Turning on…' : 'Turn on notifications'}
            </button>
        );
    }

    return (
        <div role='status' style={S.bar}>
            <span style={{ fontSize: 16 }}><FaIcon name='bell' /></span>
            <div style={S.text}>{body}</div>
            {action}
            <button type='button' title='Hide for this session' aria-label='Hide for this session' style={S.close} onClick={dismiss}><FaIcon name='times' /></button>
        </div>
    );
}
